import createClient, { type Client } from "openapi-fetch";

import { baseUrl, getToken } from "./_skedApi";

export interface GeneratedSkedApiClientConfig {
  baseUrl?: string;
  fetchImpl?: typeof fetch;
  getBearerToken?: (forceRequest?: boolean) => Promise<string>;
  maxAuthRetries?: number;
}

export interface OpenApiResponse<TData, TError = unknown> {
  data?: TData;
  error?: TError;
  response: Response;
}

export class GeneratedSkedApiError extends Error {
  status: number;
  body: string;
  response: {
    status: number;
    data: unknown;
  };

  constructor(status: number, body: string) {
    const bodySnippet = body.trim().slice(0, 300);
    super(
      bodySnippet
        ? `Generated Sked API request failed with status ${status}: ${bodySnippet}`
        : `Generated Sked API request failed with status ${status}`
    );
    this.name = "GeneratedSkedApiError";
    this.status = status;
    this.body = body;
    this.response = {
      status,
      data: parseOpenApiErrorBody(body),
    };
  }
}

export class GeneratedSkedApiClient<Paths extends object> {
  readonly client: Client<Paths>;

  constructor({
    baseUrl: apiBaseUrl = baseUrl,
    fetchImpl = getDefaultFetch(),
    getBearerToken = getToken,
    maxAuthRetries = 3,
  }: GeneratedSkedApiClientConfig = {}) {
    this.client = createClient<Paths>({
      baseUrl: getBaseUrl(apiBaseUrl),
      fetch: (request) =>
        fetchWithSkedApiAuth({
          request,
          fetchImpl,
          getBearerToken,
          maxAuthRetries,
        }),
    });
  }

  async unwrap<TData, TError = unknown>(
    result: OpenApiResponse<TData, TError>
  ): Promise<TData> {
    if (!result.response.ok) {
      throw new GeneratedSkedApiError(
        result.response.status,
        stringifyOpenApiError(result.error)
      );
    }

    if (result.data === undefined || result.data === null) {
      throw new GeneratedSkedApiError(
        result.response.status,
        "Empty response body"
      );
    }

    return result.data;
  }
}

function getBaseUrl(apiBaseUrl: string | undefined): string {
  return apiBaseUrl?.replace(/\/+$/, "") ?? "";
}

function getDefaultFetch(): typeof fetch {
  if (globalThis.fetch) {
    return globalThis.fetch.bind(globalThis);
  }

  return missingFetch;
}

const missingFetch: typeof fetch = async () => {
  throw new GeneratedSkedApiError(0, "global fetch is not available");
};

interface FetchWithSkedApiAuthOptions {
  request: Request;
  fetchImpl: typeof fetch;
  getBearerToken: (forceRequest?: boolean) => Promise<string>;
  maxAuthRetries: number;
}

async function fetchWithSkedApiAuth({
  request,
  fetchImpl,
  getBearerToken,
  maxAuthRetries,
}: FetchWithSkedApiAuthOptions): Promise<Response> {
  let token = await getBearerToken();
  let attempt = 0;
  let requestTemplate = request;

  while (true) {
    const authorizedRequest = withBearerToken(requestTemplate, token);
    requestTemplate = authorizedRequest.clone();

    const response = await fetchImpl(authorizedRequest);

    if (response.status !== 401 || attempt >= maxAuthRetries) {
      return response;
    }

    attempt += 1;
    token = await getBearerToken(true);
  }
}

function withBearerToken(request: Request, token: string): Request {
  const headers = new Headers(request.headers);
  headers.set("Authorization", `Bearer ${token}`);
  headers.set("Accept", "application/json");

  return new Request(request, { headers });
}

function stringifyOpenApiError(error: unknown): string {
  if (error === undefined || error === null) {
    return "{}";
  }

  if (typeof error === "string") {
    return error;
  }

  return JSON.stringify(error);
}

function parseOpenApiErrorBody(body: string): unknown {
  try {
    return JSON.parse(body);
  } catch {
    return {
      message: body,
    };
  }
}
